import { supabase } from "../../config/supabaseClient";
import { createComponentSlug, getComponentStatus } from "../componentsTable";
import {
  getCollectionDisplayDate,
  getProductCollectionRoute,
  isOpaqueComponentPublicId,
  parseProductIds,
  PRODUCT_COLLECTION_TYPE,
  serializeCollectionDetails,
  serializeProductIds,
} from "../productCollection";
import { buildComponentCopyName } from "../StayUpToDate/componentDuplicate";
import { hasWebsiteProduct } from "../subscriptionPlan";
import { readCachedCreatorPublicSurface } from "../url/creatorPublicSurface";
import {
  buildCreatorCollectionUrl,
  type CreatorBrowsableUrlPrefs,
} from "../url/getCreatorPublicBaseUrl";
import { getCreatorSpaRelativePath } from "../url/getCreatorSpaRelativePath";
import { allocateUniqueComponentName } from "./StayUpToDateDatabase";

const TABLE = "stayuptodate_components";

export type ProductCollectionStatus = "active" | "draft" | "archive";

export type ProductCollectionRow = {
  id: string | number;
  name: string;
  description?: string | null;
  details?: string | null;
  post_ids?: string | null;
  unique_id?: string | null;
  route_name?: string | null;
  status?: string | null;
  status_expiry?: string | null;
  user_id?: string;
  username?: string;
  type?: string;
};

async function generateUniquePublicId(userId: string) {
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const candidate = createComponentSlug();
    const { data } = await supabase
      .from(TABLE)
      .select("id")
      .eq("user_id", userId)
      .or(`unique_id.eq.${candidate},route_name.eq.${candidate}`)
      .neq("status", "deleted")
      .limit(1);
    if (!Array.isArray(data) || data.length === 0) return candidate;
  }
  return `${createComponentSlug()}${createComponentSlug()}`;
}

async function assertCollectionNameAvailable(
  userId: string,
  name: string,
  currentId?: string | number | null
) {
  let query = supabase
    .from(TABLE)
    .select("id,status,status_expiry")
    .eq("user_id", userId)
    .eq("type", PRODUCT_COLLECTION_TYPE)
    .eq("name", name)
    .neq("status", "deleted");

  if (currentId != null && String(currentId)) {
    query = query.neq("id", currentId);
  }

  const { data } = await query;
  const blocking = (Array.isArray(data) ? data : []).some(
    (row) => getComponentStatus(row) !== "hidden"
  );
  return !blocking;
}

function memberRowForUsername(username: string) {
  try {
    const parsed = JSON.parse(String(localStorage.getItem("member") || ""));
    if (parsed && String(parsed.username || "").trim() === username) {
      return parsed;
    }
  } catch {
    // ignore
  }
  return null;
}

/** Published website (97) uses `/shop/collections/:id`; otherwise LiB `/collections/:id`. */
export function shouldUseWebsiteProductCollectionUrl(args: {
  username: string;
  userData?: any;
}) {
  const row = args.userData || memberRowForUsername(args.username);
  return Boolean(
    hasWebsiteProduct(row) &&
      String(row?.browsable_website_published_at || "").trim()
  );
}

/** Public visitors may only have the surface cache; dashboard has `userData`. */
export function hasPublishedWebsiteSurface(args: {
  username: string;
  userData?: any;
}) {
  const username = String(args.username || "").trim().toLowerCase();
  if (username && readCachedCreatorPublicSurface(username) === "website") {
    return true;
  }
  return shouldUseWebsiteProductCollectionUrl(args);
}

export function getProductCollectionPublicPath(args: {
  username: string;
  publicId: string;
  pathname?: string;
  forWebsite?: boolean;
  userData?: any;
}) {
  const publicId = String(args.publicId || "").trim();
  const forWebsite =
    args.forWebsite ??
    shouldUseWebsiteProductCollectionUrl({
      username: args.username,
      userData: args.userData,
    });
  return getCreatorSpaRelativePath({
    username: args.username,
    route: getProductCollectionRoute(publicId, forWebsite),
    pathname: args.pathname,
  });
}

export function getProductCollectionPublicUrl(args: {
  username: string;
  publicId: string;
  browsableUrlPrefs?: CreatorBrowsableUrlPrefs | null;
  forWebsite?: boolean;
  userData?: any;
}) {
  const publicId = String(args.publicId || "").trim();
  if (!args.username || !publicId) return "";
  const forWebsite =
    args.forWebsite ??
    shouldUseWebsiteProductCollectionUrl({
      username: args.username,
      userData: args.userData,
    });
  return buildCreatorCollectionUrl({
    username: args.username,
    route: getProductCollectionRoute(publicId, forWebsite),
    browsableUrlPrefs: args.browsableUrlPrefs,
  });
}

export async function getStayUpToDateComponentTypeByPublicSlug(args: {
  publicId: string;
  userId?: string | null;
  username?: string | null;
}) {
  const publicId = String(args.publicId || "").trim();
  if (!publicId || !isOpaqueComponentPublicId(publicId)) return "";

  let query = supabase
    .from(TABLE)
    .select("type")
    .neq("status", "deleted")
    .or(`unique_id.eq.${publicId},route_name.eq.${publicId}`);

  if (args.userId) query = query.eq("user_id", args.userId);
  else if (args.username) query = query.ilike("username", args.username);

  const { data, error } = await query.limit(1).maybeSingle();
  if (error) throw error;
  return String((data as { type?: string } | null)?.type || "");
}

const PRODUCT_COLLECTION_LIST_COLS =
  "id,name,details,post_ids,unique_id,route_name,status,status_expiry,user_id,username,type";

export async function listProductCollections(
  userId: string,
  opts?: { lean?: boolean },
) {
  const { data, error } = await supabase
    .from(TABLE)
    .select(opts?.lean ? PRODUCT_COLLECTION_LIST_COLS : "*")
    .eq("user_id", userId)
    .eq("type", PRODUCT_COLLECTION_TYPE)
    .neq("status", "deleted")
    .order("id", { ascending: false });

  if (error) throw error;
  return (Array.isArray(data) ? data : []) as ProductCollectionRow[];
}

export async function getProductCollectionByPublicId(args: {
  publicId: string;
  userId?: string | null;
  username?: string | null;
}) {
  const publicId = String(args.publicId || "").trim();
  if (!publicId || !isOpaqueComponentPublicId(publicId)) return null;

  let query = supabase
    .from(TABLE)
    .select("*")
    .eq("type", PRODUCT_COLLECTION_TYPE)
    .neq("status", "deleted")
    .or(`unique_id.eq.${publicId},route_name.eq.${publicId}`);

  if (args.userId) query = query.eq("user_id", args.userId);
  else if (args.username) query = query.ilike("username", args.username);

  const { data, error } = await query.limit(1).maybeSingle();
  if (error) throw error;
  return (data as ProductCollectionRow) || null;
}

/** Mosaic thumbs only — keep the cover payload tiny. */
const COLLECTION_COVER_COLS =
  "post_id, product_image, media_url, thumbnail";

/** Chunk size for `.in(post_id, …)` so URL length stays under PostgREST limits. */
const PRODUCT_ID_IN_CHUNK = 120;

export async function fetchCollectionProducts(
  username: string,
  userId: string,
  productIds: string[]
) {
  if (!username || !productIds.length) return [];
  const unique: string[] = [];
  const seen = new Set<string>();
  for (const raw of productIds) {
    const id = String(raw || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    unique.push(id);
  }
  const byId = new Map<string, any>();
  for (let i = 0; i < unique.length; i += PRODUCT_ID_IN_CHUNK) {
    const chunk = unique.slice(i, i + PRODUCT_ID_IN_CHUNK);
    const { data, error } = await supabase
      .from(username)
      .select("*")
      .eq("user_id", userId)
      .in("post_id", chunk);
    if (error) throw error;
    for (const row of Array.isArray(data) ? data : []) {
      const id = String((row as any)?.post_id || "").trim();
      if (id) byId.set(id, row);
    }
  }
  return productIds
    .map((id) => byId.get(String(id)))
    .filter(Boolean);
}

/**
 * Lean batch fetch for collection mosaic thumbs — one (chunked) round-trip
 * for many post ids instead of N per-collection queries.
 */
export async function fetchCollectionProductCoversByIds(
  username: string,
  userId: string,
  productIds: string[]
) {
  if (!username || !userId || !productIds.length) {
    return new Map<string, any>();
  }
  const unique: string[] = [];
  const seen = new Set<string>();
  for (const raw of productIds) {
    const id = String(raw || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    unique.push(id);
  }
  const byId = new Map<string, any>();
  for (let i = 0; i < unique.length; i += PRODUCT_ID_IN_CHUNK) {
    const chunk = unique.slice(i, i + PRODUCT_ID_IN_CHUNK);
    const { data, error } = await supabase
      .from(username)
      .select(COLLECTION_COVER_COLS)
      .eq("user_id", userId)
      .in("post_id", chunk);
    if (error) throw error;
    for (const row of Array.isArray(data) ? data : []) {
      const id = String((row as any)?.post_id || "").trim();
      if (id) byId.set(id, row);
    }
  }
  return byId;
}

export async function createProductCollection(args: {
  userId: string;
  username: string;
  name: string;
  description?: string;
  collectionDate?: string | null;
  productIds: string[];
  status: ProductCollectionStatus;
  /** Match custom-products duplicate: titles may collide after `(copy)`. */
  allowDuplicateName?: boolean;
}) {
  const name = String(args.name || "").trim();
  if (!name) return "Title is required";
  if (!args.allowDuplicateName) {
    const available = await assertCollectionNameAvailable(args.userId, name);
    if (!available) return "Duplicate Name";
  }

  const uniqueSlug = await generateUniquePublicId(args.userId);
  const payload: Record<string, unknown> = {
    name,
    description: String(args.description || "").trim(),
    details: serializeCollectionDetails(args.collectionDate),
    user_id: args.userId,
    username: args.username,
    index: 0,
    type: PRODUCT_COLLECTION_TYPE,
    unique_id: uniqueSlug,
    route_name: uniqueSlug,
    post_ids: serializeProductIds(args.productIds),
    display_name: "Product Collection",
    status: args.status,
    status_expiry: args.status,
  };

  let { error } = await supabase.from(TABLE).insert([payload]);
  if (error && String(error.message || "").toLowerCase().includes("description")) {
    const { description: _omit, ...withoutDescription } = payload;
    ({ error } = await supabase.from(TABLE).insert([withoutDescription]));
  }
  if (error) throw error;
  return "true";
}

export async function updateProductCollection(args: {
  collection: ProductCollectionRow;
  userId: string;
  name: string;
  description?: string;
  collectionDate?: string | null;
  productIds: string[];
  status?: ProductCollectionStatus;
}) {
  const name = String(args.name || "").trim();
  if (!name) return "Title is required";
  const available = await assertCollectionNameAvailable(
    args.userId,
    name,
    args.collection.id
  );
  if (!available) return "Duplicate Name";

  const nextStatus =
    args.status ||
    ((getComponentStatus(args.collection) as ProductCollectionStatus) || "active");
  const { error } = await supabase
    .from(TABLE)
    .update({
      name,
      description: String(args.description || "").trim(),
      details: serializeCollectionDetails(
        args.collectionDate ?? getCollectionDisplayDate(args.collection)
      ),
      post_ids: serializeProductIds(args.productIds),
      status: nextStatus,
      status_expiry: nextStatus,
    })
    .eq("id", args.collection.id)
    .eq("user_id", args.userId)
    .eq("type", PRODUCT_COLLECTION_TYPE);

  if (error) throw error;
  return "true";
}

export async function deleteProductCollection(args: {
  collectionId: string | number;
  userId: string;
}) {
  const { error } = await supabase
    .from(TABLE)
    .update({ status: "deleted", status_expiry: "deleted" })
    .eq("id", args.collectionId)
    .eq("user_id", args.userId)
    .eq("type", PRODUCT_COLLECTION_TYPE);

  if (error) throw error;
}

/**
 * Instant clone. Title rules match custom products duplication
 * (`buildComponentCopyName` + `allocateUniqueComponentName`).
 * Insert reuses `createProductCollection` (new public id, same products).
 */
export async function duplicateProductCollection(args: {
  collection: ProductCollectionRow;
  userId: string;
  username: string;
}) {
  const source = args.collection;
  if (!source?.id || !args.userId || !args.username) {
    throw new Error("Unable to duplicate collection");
  }

  const copyName = await allocateUniqueComponentName({
    tableName: TABLE,
    userId: args.userId,
    desiredName: buildComponentCopyName(source.name),
  });

  const sourceStatus = getComponentStatus(source);
  const result = await createProductCollection({
    userId: args.userId,
    username: args.username,
    name: copyName,
    description: String(source.description || "").trim(),
    collectionDate: getCollectionDisplayDate(source),
    productIds: parseProductIds(source.post_ids),
    status:
      sourceStatus === "draft" || sourceStatus === "active"
        ? sourceStatus
        : "active",
    allowDuplicateName: true,
  });

  if (result !== "true") {
    throw new Error(String(result || "Failed to duplicate collection"));
  }
  return copyName;
}

export function collectionProductCount(row: ProductCollectionRow) {
  return parseProductIds(row.post_ids).length;
}
